import type { StaffTeamLoad } from "@lafayette/schemas";

import { gapKindShort } from "../team/labels";

// The words and numbers of the team load card (CHT-2026), apart from the drawing. Everything here is read from the API answer: the card adds
// no rule, no threshold and no capacity. A bar is a person's customers next to the person with the most.

type Member = StaffTeamLoad["members"][number];
type NoCover = StaffTeamLoad["noCover"];

/** The people who can take work now come first (most customers first, then by name); the people who are away come last. */
export function orderedMembers(members: readonly Member[]): readonly Member[] {
  return [...members].sort(
    (a, b) =>
      Number(a.awayUntil !== null) - Number(b.awayUntil !== null) ||
      b.customers - a.customers ||
      a.displayName.localeCompare(b.displayName),
  );
}

/** How wide a bar is, 0 to 100: the share of the largest load on the team. Nobody has customers: every bar is empty. */
export function barPercent(customers: number, members: readonly Member[]): number {
  const most = Math.max(0, ...members.map((member) => member.customers));
  return most === 0 ? 0 : Math.round((customers / most) * 100);
}

/** `3 of 4 available`. */
export function availableText(members: readonly Member[]): string {
  const available = members.filter((member) => member.awayUntil === null).length;
  return `${available} of ${members.length} available`;
}

export const customersText = (count: number): string =>
  `${count} ${count === 1 ? "customer" : "customers"}`;

/** `2 conversations wait`, `50+ conversations wait`; `null` when none wait. */
export function waitingText(member: Member): string | null {
  if (member.conversations === 0) return null;
  const count = `${member.conversations}${member.conversationsCapped ? "+" : ""}`;
  return member.conversations === 1 && !member.conversationsCapped
    ? "1 conversation waits"
    : `${count} conversations wait`;
}

/** `Away until Fri 31 Oct`, in the day of the staff member's own machine, like the greeting. */
export function awayText(iso: string): string {
  const day = new Intl.DateTimeFormat("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
  }).format(new Date(iso));
  return `Away until ${day}`;
}

/** `6 customers have no cover`; `200+ customers have no cover` when the read stopped at its cap. */
export function noCoverTitle(noCover: NoCover): string {
  const count = `${noCover.customerCount}${noCover.capped ? "+" : ""}`;
  return noCover.customerCount === 1 && !noCover.capped
    ? "1 customer has no cover"
    : `${count} customers have no cover`;
}

/** Why, in the words of the Coverage area: `No primary · Primary unavailable`. */
export const noCoverReasons = (noCover: NoCover): string =>
  noCover.reasons.map((reason) => gapKindShort[reason]).join(" · ");

/** The customers the alert names, for a screen reader: `Anika Rao and Meera Iyer`, `Anika Rao, Meera Iyer and 4 more`. */
export function namedCustomers(noCover: NoCover): string {
  const names = noCover.customers.map((customer) => customer.displayName);
  const more = noCover.customerCount - names.length;
  if (more > 0) return `${names.join(", ")} and ${more}${noCover.capped ? "+" : ""} more`;
  if (names.length < 2) return names.join("");
  return `${names.slice(0, -1).join(", ")} and ${names.at(-1)}`;
}
